/*
  <engraving-preview> + window.BEEngraving

  A standalone renderer of an engraved metal plate (wallet card, bookmark, plate).
  Server-rendered first by snippets/engraving-preview.liquid; this script keeps it
  live. Contract: scratchpad/spec/PERSONALIZER-CONTRACT.md.
*/
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Shared helpers (window.BEEngraving). Defined once even if this file loads twice.
  // ---------------------------------------------------------------------------
  var BE = window.BEEngraving = window.BEEngraving || {};

  if (!BE._core) {
    BE._core = true;

    BE.METALS = {
      aluminum: {
        label: 'Aluminum',
        swatch: 'linear-gradient(135deg,#c9cacc,#eeeff0)',
        plate: 'linear-gradient(135deg,#d3d4d6 0%,#f3f4f4 38%,#c6c8ca 68%,#e4e5e6 100%)',
        ink: '#5b5e62'
      },
      stainless: {
        label: 'Stainless steel',
        swatch: 'linear-gradient(135deg,#8e9093,#c4c6c8)',
        plate: 'linear-gradient(135deg,#9a9c9f,#cbcdcf 40%,#8c8e91 70%,#b6b8bb)',
        ink: '#3b3e41'
      },
      brass: {
        label: 'Brass',
        swatch: 'linear-gradient(135deg,#b08a32,#e3c76c)',
        plate: 'linear-gradient(135deg,#b8923c,#e2c46b 40%,#a9842f 70%,#d3b459)',
        ink: '#5a4412'
      },
      copper: {
        label: 'Copper',
        swatch: 'linear-gradient(135deg,#a05c34,#d9976c)',
        plate: 'linear-gradient(135deg,#a8623a,#d5946c 40%,#96552f 70%,#c5825a)',
        ink: '#4a2813'
      }
    };

    // Script faces render optically small, so the preview scales them up.
    BE.SCRIPT_FONTS = [
      'Pinyon Script', 'Great Vibes', 'Allura', 'Alex Brush', 'Dancing Script', 'Sacramento',
      'Satisfy', 'Pacifico', 'Tangerine', 'Parisienne', 'Yellowtail', 'Kaushan Script',
      'Courgette', 'Niconne', 'Cookie', 'Petit Formal Script', 'Marck Script', 'Norican',
      'Clicker Script', 'Mr Dafoe', 'Carattere', 'Rouge Script', 'Italianno', 'Engagement',
      'Bilbo', 'Mrs Saint Delafield', 'Herr Von Muellerhoff', 'Lovers Quarrel',
      'Monsieur La Doulaise', 'Mr De Haviland', 'Style Script', 'Ephesis', 'Birthstone',
      'Whisper', 'Meddon'
    ];

    // Known properties[Font] values. "Custom: X" values are resolved by fontFor().
    BE.FONT_PREVIEW = {
      'Sans Serif (Optima)': { family: 'Marcellus', scale: 1, label: 'Sans serif', fallback: 'serif' },
      'Custom: EB Garamond': { family: 'EB Garamond', scale: 1.08, label: 'Serif', fallback: 'serif' },
      'Script (Cornet)': { family: 'Pinyon Script', scale: 1.3, label: 'Script', fallback: 'cursive' }
    };

    BE.SIZES = {
      s: { label: 'Small', scale: 0.85 },
      m: { label: 'Medium', scale: 1 },
      l: { label: 'Large', scale: 1.2 }
    };

    BE.ALIGNS = {
      left: { label: 'Left' },
      center: { label: 'Center' },
      right: { label: 'Right' }
    };

    var unquote = function (f) {
      return String(f || '').split(',')[0].replace(/["']/g, '').trim();
    };

    BE.scaleForFamily = function (family) {
      family = unquote(family);
      if (family === 'EB Garamond') return 1.08;
      return BE.SCRIPT_FONTS.indexOf(family) !== -1 ? 1.3 : 1;
    };

    // properties[Font] value -> { family, css, scale, label }. Unknown -> sans.
    BE.fontFor = function (value) {
      var hit = BE.FONT_PREVIEW[value];
      var family, fallback;
      if (hit) {
        family = hit.family;
        fallback = hit.fallback;
      } else if (typeof value === 'string' && value.indexOf('Custom: ') === 0) {
        family = value.slice(8).trim();
        fallback = BE.SCRIPT_FONTS.indexOf(family) !== -1 ? 'cursive' : 'serif';
      } else {
        return BE.fontFor('Sans Serif (Optima)');
      }
      return {
        family: family,
        css: "'" + family + "', " + fallback,
        scale: hit ? hit.scale : BE.scaleForFamily(family),
        label: hit ? hit.label : family
      };
    };

    BE.normalizeAlign = function (a) {
      a = String(a || '').toLowerCase();
      return BE.ALIGNS[a] ? a : '';
    };

    BE.normalizeSize = function (s) {
      s = String(s || '').toLowerCase();
      if (BE.SIZES[s]) return s;
      if (s === 'small') return 's';
      if (s === 'medium') return 'm';
      if (s === 'large') return 'l';
      return '';
    };

    BE.normalizeSide = function (s) {
      s = String(s || '').toLowerCase();
      return s === 'front' || s === 'back' ? s : '';
    };

    // Lazy Google Fonts loader, shared with engraving-personalizer.js through
    // window.BEEngravingFonts so a family is only requested once per page.
    var loadedFonts = window.BEEngravingFonts = window.BEEngravingFonts || {};
    BE.loadGoogleFont = function (families) {
      if (!Array.isArray(families)) families = [families];
      var batch = [];
      families.forEach(function (f) {
        f = unquote(f);
        if (f && !loadedFonts[f]) { loadedFonts[f] = true; batch.push(f); }
      });
      for (var i = 0; i < batch.length; i += 18) {
        var group = batch.slice(i, i + 18);
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?' +
          group.map(function (f) {
            return 'family=' + encodeURIComponent(f).replace(/%20/g, '+');
          }).join('&') + '&display=swap';
        document.head.appendChild(link);
      }
    };

    BE.TALLY_MIN = 1;
    BE.TALLY_MAX = 50;

    BE.clampTally = function (n) {
      n = parseInt(n, 10);
      if (!(n >= BE.TALLY_MIN)) return BE.TALLY_MIN;
      return Math.min(BE.TALLY_MAX, n);
    };

    // One row of tally marks as SVG markup, drawn in currentColor. Same geometry as
    // snippets/be-tally-svg.liquid (keep the two in step): stroke k of group g at x = g*30 + k*6,
    // fifth mark from (g*30 - 3, 18) to (g*30 + 21, 6); viewBox 24 units tall.
    BE.tallySvg = function (count) {
      var n = BE.clampTally(count);
      var d = '';
      var gx = 0;
      for (var i = 0; i < n; i++) {
        var pos = i % 5;
        if (pos < 4) {
          var x = gx + pos * 6;
          d += 'M' + (x + 1.5) + ' 1L' + x + ' 23';
        } else {
          d += 'M' + (gx - 3) + ' 18L' + (gx + 21) + ' 6';
          gx += 30;
        }
      }
      var rem = n % 5;
      var max = rem > 0 ? gx + (rem - 1) * 6 + 2.7 : gx - 30 + 22.2;
      var min = n >= 5 ? -4.2 : -1.2;
      var w = Math.round((max - min) * 10) / 10;
      return '<svg class="be-tally" viewBox="' + min + ' 0 ' + w + ' 24" preserveAspectRatio="xMaxYMax meet" aria-hidden="true" focusable="false">' +
        '<path d="' + d + '" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
    };

    // Hand-drawn heart for the 'fixed_heart' corner (same path as snippets/be-heart-svg.liquid).
    BE.HEART_SVG = '<svg class="be-heart" viewBox="-1 -1 102 87.2" aria-hidden="true" focusable="false">' +
      '<path d="M18.1 0C19.8 -0.2 21.1 -0.1 22.5 0C24 0.2 24.4 -0.1 26.8 0.9C29.2 1.9 34.3 4.3 36.9 6.2C39.5 8.2 40.3 9.5 42.3 12.6C44.3 15.7 47.4 22.8 48.7 24.7C50 26.6 48.7 25.3 50.1 24.2C51.5 23.1 54.9 20 57 18.3C59.2 16.7 61 15.4 63 14.3C65 13.2 67.1 12.3 69.1 11.5C71.1 10.8 73.2 10.2 75.2 9.8C77.2 9.5 79.5 9.3 81.3 9.4C83.1 9.5 84.6 9.7 86.3 10.2C88 10.7 89.8 11.5 91.3 12.4C92.8 13.3 94.2 14.5 95.3 15.7C96.4 16.9 97.1 18 97.8 19.4C98.5 20.8 99.1 21.9 99.5 24C99.9 26.2 100.1 30.3 100 32.3C100 34.3 100.4 33.6 99.2 35.9C98 38.2 94.6 43.4 92.9 45.9C91.2 48.4 90.4 49.3 88.8 51.1C87.2 52.9 85.3 54.9 83.1 56.8C80.9 58.8 81.9 58.2 75.6 62.8C69.3 67.4 50.7 80.8 45.1 84.5C39.5 88.2 43.2 85.4 42.2 85.2C41.3 85 42.6 87.2 39.4 83.3C36.2 79.4 27.4 67.9 22.8 61.7C18.2 55.5 14.4 50.1 11.7 45.9C9 41.7 8.6 40.7 6.7 36.6C4.8 32.5 1.6 24.4 0.5 21.2C-0.6 18 0 18.8 0 17.6C0 16.4 -0.3 15.8 0.5 14C1.3 12.2 3.4 8.4 4.6 6.8C5.8 5.2 6.2 5.1 7.5 4.2C8.8 3.3 10.7 2.2 12.5 1.5C14.3 0.8 16.4 0.3 18.1 0ZM19.9 7.9C21.7 7.6 22.5 7.6 24 7.7C25.5 7.8 27.6 8.3 29 8.7C30.4 9.1 31.4 9.5 32.6 10.2C33.8 10.9 34.9 11.7 36.1 13.1C37.3 14.5 38.4 15.2 39.8 18.6C41.2 22 43.2 31 44.2 33.7C45.2 36.4 43.7 36.6 45.8 34.9C47.9 33.2 54.2 26 57 23.3C59.8 20.7 60.8 20.3 62.7 19C64.6 17.7 66.4 16.7 68.4 15.7C70.4 14.7 72.8 13.8 74.5 13.2C76.2 12.6 77.4 12.4 78.8 12.4C80.2 12.4 81.4 12.6 82.7 13C84 13.4 85.5 14.1 86.7 15C87.9 15.9 89.2 17.2 90.1 18.3C91 19.5 91.6 20.7 92.1 21.9C92.6 23.2 92.8 24.4 92.8 25.8C92.8 27.2 92.6 28.6 92.3 30.1C92 31.6 91.4 33.2 90.7 34.8C90 36.4 89.9 37.3 87.9 39.8C85.9 42.3 81.8 47.1 78.8 50.1C75.8 53.2 74.2 54.7 69.7 58.1C65.2 61.6 55.4 68 51.6 70.8C47.8 73.6 48.1 74.1 46.9 74.9C45.7 75.7 45.2 75.8 44.4 75.8C43.6 75.8 46 79.9 42.2 75C38.4 70.1 26 53.3 21.4 46.6C16.8 40 16.7 38.8 14.9 35.1C13.1 31.5 11.5 27.6 10.5 24.7C9.5 21.8 9.1 19.9 8.9 17.9C8.7 15.9 9.1 14.1 9.5 12.9C9.9 11.8 10.5 11.6 11.1 11C11.7 10.5 11.7 10.1 13.2 9.6C14.7 9.1 18.1 8.2 19.9 7.9Z" fill="currentColor" fill-rule="evenodd"/></svg>';

    var PARAM_KEYS = ['front', 'back', 'font', 'align', 'size', 'side', 'tally', 'name', 'heart', 'replace'];

    // Build a product URL that carries the engraving along (query-param prefill).
    // state: { front, back, fontValue|font, align, size, side, replace }. Empty keys are omitted.
    BE.buildUrl = function (productUrl, state) {
      state = state || {};
      var url = String(productUrl || '');
      var hash = '';
      var h = url.indexOf('#');
      if (h !== -1) { hash = url.slice(h); url = url.slice(0, h); }
      var query = '';
      var q = url.indexOf('?');
      if (q !== -1) { query = url.slice(q + 1); url = url.slice(0, q); }

      var parts = query ? query.split('&').filter(function (p) {
        if (!p) return false;
        var key = decodeURIComponent(p.split('=')[0].replace(/\+/g, ' '));
        return PARAM_KEYS.indexOf(key) === -1;
      }) : [];

      var add = function (key, value) {
        if (value === undefined || value === null) return;
        value = String(value).replace(/\r\n?/g, '\n');
        if (!value) return;
        parts.push(key + '=' + encodeURIComponent(value));
      };
      add('front', state.front);
      add('back', state.back);
      add('font', state.fontValue !== undefined ? state.fontValue : state.font);
      add('align', BE.normalizeAlign(state.align));
      add('size', BE.normalizeSize(state.size));
      add('side', BE.normalizeSide(state.side));
      if (state.tally !== undefined && state.tally !== null && state.tally !== '') add('tally', BE.clampTally(state.tally));
      add('name', state.name);
      if (typeof state.heart === 'boolean') add('heart', state.heart ? 'yes' : 'no');
      add('replace', state.replace);

      return url + (parts.length ? '?' + parts.join('&') : '') + hash;
    };

    // Parse the prefill params from a URL (default: current page) into the same shape.
    BE.readParams = function (search) {
      var out = {};
      var src = search === undefined ? window.location.search : String(search);
      var q = src.indexOf('?');
      if (q !== -1) src = src.slice(q + 1);
      var h = src.indexOf('#');
      if (h !== -1) src = src.slice(0, h);
      src.split('&').forEach(function (p) {
        if (!p) return;
        var i = p.indexOf('=');
        var key, val;
        try {
          key = decodeURIComponent((i === -1 ? p : p.slice(0, i)).replace(/\+/g, ' '));
          val = i === -1 ? '' : decodeURIComponent(p.slice(i + 1).replace(/\+/g, ' '));
        } catch (e) { return; }
        val = val.replace(/\r\n?/g, '\n');
        if (key === 'front' || key === 'back' || key === 'replace') out[key] = val;
        else if (key === 'font') out.fontValue = val;
        else if (key === 'align' && BE.normalizeAlign(val)) out.align = BE.normalizeAlign(val);
        else if (key === 'size' && BE.normalizeSize(val)) out.size = BE.normalizeSize(val);
        else if (key === 'side' && BE.normalizeSide(val)) out.side = BE.normalizeSide(val);
        else if (key === 'tally' && parseInt(val, 10) > 0) out.tally = BE.clampTally(val);
        else if (key === 'name') out.name = val;
        else if (key === 'heart' && /^(yes|no)$/i.test(val)) out.heart = val.toLowerCase() === 'yes';
      });
      return out;
    };
  }

  // ---------------------------------------------------------------------------
  // <engraving-preview>
  // ---------------------------------------------------------------------------
  if (!('customElements' in window) || customElements.get('engraving-preview')) return;

  function clampLines(text, maxLines, maxChars) {
    return String(text || '').replace(/\r/g, '').split('\n').slice(0, maxLines).map(function (l) {
      return l.slice(0, maxChars);
    });
  }

  class EngravingPreview extends HTMLElement {
    constructor() {
      super();
      this._onChange = this._onChange.bind(this);
    }

    connectedCallback() {
      if (!this._setup) {
        this._setup = true;
        var ds = this.dataset;
        this.plate = this.querySelector('[data-preview-plate]');
        this.linesEl = this.querySelector('[data-preview-lines]');
        this.captionEl = this.querySelector('[data-preview-caption]');
        this.maxLines = parseInt(ds.maxLines, 10) || 8;
        this.maxChars = parseInt(ds.maxChars, 10) || 30;
        this.placeholder = ds.placeholder !== undefined ? ds.placeholder : 'Your text here';
        this.state = {
          front: ds.front || '',
          back: ds.back || '',
          fontValue: ds.fontValue || '',
          fontFamily: ds.fontFamily || '',
          align: BE.normalizeAlign(ds.align) || 'left',
          size: BE.normalizeSize(ds.size) || 'm',
          side: BE.normalizeSide(ds.sideLock) || BE.normalizeSide(ds.side) || 'front',
          metal: BE.METALS[ds.metal] ? ds.metal : 'stainless',
          shape: ds.shape || 'card',
          design: ds.design === 'fixed_tally' || ds.design === 'fixed_heart' ? ds.design : 'free',
          tally: BE.clampTally(ds.tally || 11),
          name: ds.name || '',
          heart: ds.heart !== 'no'
        };

        var el = this;
        this.querySelectorAll('[data-preview-side-btn]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var side = btn.getAttribute('data-preview-side-btn');
            el.setSide(side);
            if (el.dataset.section && !el.isStatic()) {
              // Ask the bound personalizer to switch its Front/Back tab too.
              document.dispatchEvent(new CustomEvent('engraving:side-request', {
                detail: { sectionId: el.dataset.section, side: side }
              }));
            }
          });
        });
      }

      if (this.dataset.section && !this.isStatic()) {
        document.addEventListener('engraving:change', this._onChange);
        // The personalizer may have initialised (and fired its init event) before
        // this element upgraded, so pull its current state directly.
        var p = document.querySelector('engraving-personalizer[data-section="' + this.dataset.section + '"]');
        if (p && typeof p.getState === 'function') {
          this.update(p.getState());
          return;
        }
      }
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('engraving:change', this._onChange);
    }

    isStatic() {
      var s = this.dataset.static;
      return s !== undefined && s !== 'false';
    }

    _onChange(e) {
      var d = e.detail;
      if (!d || String(d.sectionId) !== String(this.dataset.section)) return;
      this.update(d);
    }

    getState() {
      var s = {};
      for (var k in this.state) s[k] = this.state[k];
      return s;
    }

    update(patch) {
      if (!patch) return;
      var s = this.state;
      if (typeof patch.front === 'string') s.front = patch.front;
      if (typeof patch.back === 'string') s.back = patch.back;
      if (typeof patch.fontValue === 'string') {
        s.fontValue = patch.fontValue;
        // A value without an explicit family resets the family to the value's own.
        if (typeof patch.fontFamily !== 'string') s.fontFamily = '';
      }
      if (typeof patch.fontFamily === 'string') s.fontFamily = patch.fontFamily;
      if (BE.normalizeAlign(patch.align)) s.align = BE.normalizeAlign(patch.align);
      if (BE.normalizeSize(patch.size)) s.size = BE.normalizeSize(patch.size);
      // data-side-lock: this preview always shows one side (both sides shown at once).
      if (BE.normalizeSide(patch.side) && !this.dataset.sideLock) s.side = BE.normalizeSide(patch.side);
      if (patch.metal && BE.METALS[patch.metal]) s.metal = patch.metal;
      if (typeof patch.shape === 'string' && patch.shape) s.shape = patch.shape;
      if (patch.design === 'fixed_tally' || patch.design === 'fixed_heart' || patch.design === 'free') s.design = patch.design;
      if (patch.tally !== undefined && patch.tally !== null && patch.tally !== '') s.tally = BE.clampTally(patch.tally);
      if (typeof patch.name === 'string') s.name = patch.name;
      if (typeof patch.heart === 'boolean') s.heart = patch.heart;
      this.render();
    }

    setSide(side) {
      this.update({ side: side });
    }

    resolveFont() {
      var s = this.state;
      var f = BE.fontFor(s.fontValue);
      if (s.fontFamily) {
        var fam = s.fontFamily.split(',')[0].replace(/["']/g, '').trim();
        var fromValue = s.fontValue && (BE.FONT_PREVIEW[s.fontValue] || s.fontValue.indexOf('Custom: ') === 0);
        return {
          family: fam,
          css: "'" + fam + "', " + (BE.SCRIPT_FONTS.indexOf(fam) !== -1 ? 'cursive' : 'serif'),
          scale: fromValue ? f.scale : BE.scaleForFamily(fam)
        };
      }
      return f;
    }

    render() {
      var s = this.state;
      var font = this.resolveFont();

      this.setAttribute('data-shape', s.shape);
      this.setAttribute('data-metal', s.metal);
      this.setAttribute('data-side', s.side);
      this.setAttribute('data-align', s.align);
      this.setAttribute('data-size', s.size);
      this.style.setProperty('--be-preview-font', font.css);
      this.style.setProperty('--be-preview-font-scale', String(font.scale));
      BE.loadGoogleFont(font.family);

      this.querySelectorAll('[data-preview-side-btn]').forEach(function (btn) {
        var on = btn.getAttribute('data-preview-side-btn') === s.side;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.captionEl) {
        var cap = 'Live preview · ' + (s.side === 'back' ? 'back' : 'front');
        if (this.dataset.captionMetal === 'true') cap += ' · ' + BE.METALS[s.metal].label;
        this.captionEl.textContent = cap;
      }

      if (!this.linesEl) return;
      if (s.design !== 'free' && s.side === 'front') {
        this.renderFixed();
        return;
      }
      this.linesEl.classList.remove('be-preview__lines--fixed');
      if (this.plate) {
        this.plate.querySelectorAll('[data-preview-tally], [data-preview-corner]').forEach(function (n) { n.remove(); });
      }

      var lines = clampLines(s[s.side], this.maxLines, this.maxChars);
      var empty = !lines.join('').trim();
      var frag = document.createDocumentFragment();
      if (empty) {
        lines = this.placeholder ? [this.placeholder] : [];
      }
      lines.forEach(function (l) {
        var div = document.createElement('div');
        div.className = 'be-preview__line';
        div.textContent = l || ' ';
        frag.appendChild(div);
      });
      this.linesEl.replaceChildren(frag);
      this.linesEl.classList.toggle('is-placeholder', empty);

      this.fit();
      var el = this;
      if (document.fonts && document.fonts.load) {
        document.fonts.load('16px "' + font.family + '"').then(function () { el.fit(); }, function () {});
      }
    }

    // 'fixed_tally' front: line 1 large in the chosen font, the rest as one date line in the same
    // font, and the tally marks in one row bottom right (snippets/engraving-preview.liquid SSR).
    renderFixed() {
      var s = this.state;
      var raw = String(s.front || '').replace(/\r/g, '').split('\n');
      var head = (raw[0] || '').trim();
      var date = raw.slice(1).map(function (l) { return l.trim(); }).filter(Boolean).join(' ').slice(0, this.maxChars);
      var frag = document.createDocumentFragment();
      var h = document.createElement('div');
      h.className = 'be-preview__line be-preview__line--head';
      h.textContent = head || ' ';
      frag.appendChild(h);
      if (date || this.placeholder) {
        var dl = document.createElement('div');
        dl.className = 'be-preview__line be-preview__line--date' + (date ? '' : ' is-empty');
        dl.textContent = date || 'Your date';
        frag.appendChild(dl);
      }
      this.linesEl.replaceChildren(frag);
      this.linesEl.classList.remove('is-placeholder');
      this.linesEl.classList.add('be-preview__lines--fixed');
      this.style.setProperty('--be-preview-fit', '1');

      if (s.design === 'fixed_heart') {
        this.renderCorner();
        return;
      }
      var oldCorner = this.plate.querySelector('[data-preview-corner]');
      if (oldCorner) oldCorner.remove();
      var tally = this.plate.querySelector('[data-preview-tally]');
      if (!tally) {
        tally = document.createElement('div');
        tally.className = 'be-preview__tally';
        tally.setAttribute('data-preview-tally', '');
        tally.setAttribute('aria-hidden', 'true');
        this.plate.appendChild(tally);
      }
      if (tally.dataset.count !== String(s.tally)) {
        tally.dataset.count = String(s.tally);
        tally.innerHTML = BE.tallySvg(s.tally);
      }
    }

    // 'fixed_heart' corner: outline heart (optional) + the name, bottom right, in the chosen font.
    renderCorner() {
      var s = this.state;
      var oldTally = this.plate.querySelector('[data-preview-tally]');
      if (oldTally) oldTally.remove();
      var name = String(s.name || '').trim();
      var corner = this.plate.querySelector('[data-preview-corner]');
      if (!name && !s.heart && !this.placeholder) {
        if (corner) corner.remove();
        return;
      }
      if (!corner) {
        corner = document.createElement('div');
        corner.setAttribute('data-preview-corner', '');
        corner.setAttribute('aria-hidden', 'true');
        this.plate.appendChild(corner);
      }
      corner.className = 'be-preview__corner' + (name ? '' : ' is-empty');
      var html = s.heart ? BE.HEART_SVG : '';
      corner.innerHTML = html;
      if (name || this.placeholder) {
        var span = document.createElement('span');
        span.className = 'be-preview__corner-name';
        span.textContent = name || 'Name';
        corner.appendChild(span);
      }
    }

    // Shrink the text (never grow it) when lines overflow the plate. The ratio is
    // width-independent because all sizes are in container units, so measuring once
    // per render is enough. Skipped while the element is not laid out.
    fit() {
      var plate = this.plate, box = this.linesEl;
      if (!plate || !box) return;
      this.style.setProperty('--be-preview-fit', '1');
      var availH = plate.clientHeight;
      if (!availH || !box.clientWidth) return;
      var k = 1;
      if (box.scrollHeight > availH) k = availH / box.scrollHeight;
      box.querySelectorAll('.be-preview__line').forEach(function (line) {
        if (line.scrollWidth > line.clientWidth && line.clientWidth) {
          k = Math.min(k, line.clientWidth / line.scrollWidth);
        }
      });
      if (k < 1) this.style.setProperty('--be-preview-fit', String(Math.max(0.3, k * 0.98)));
    }
  }

  customElements.define('engraving-preview', EngravingPreview);
})();
